import { useCallback, useEffect, useRef, useState } from 'react';
import {
  cameraKeepingPoint,
  clampToRect,
  fitCamera,
  getHomeRect,
  getOverviewRect,
  getViewRotation,
  screenToWorld,
  worldToScreen,
} from '../utils/boardGeometry';

export const MAX_ZOOM = 2;
const FIT_PADDING_PX = 24;
const WHEEL_ZOOM_PER_PX = 0.0015;
const WHEEL_LINE_PX = 16;

const NO_INSETS = { top: 0, bottom: 0 };

/**
 * Kamera der Brettfläche: Schwenken, Zoomen, „Eigene Fläche", „Übersicht".
 *
 *   const camera = useCamera({ viewportRef, playerCount, viewerPosition, insets: { top: headsPx, bottom: dockPx } });
 *   <div ref={camera.bindViewport} {...camera.handlers} />   // macht Battlefield
 *
 * - `bindViewport` ist der Callback-Ref für das Brett-Element; er setzt zusätzlich
 *   `viewportRef.current`. Die Größe misst ein ResizeObserver; ein neu eingehängtes Element (z. B.
 *   nach dem Hochformat-Hinweis) wird neu beobachtet.
 * - `camera` ist `null`, solange der Viewport kleiner als zweimal der Rand ist (vor dem ersten
 *   Layout): dort passt nichts hinein, `fitCamera` gäbe nur seinen Mindest-Zoom. Danach startet die Kamera auf der eigenen
 *   Fläche (`goHome`), ebenso bei geänderter Teilnehmerzahl oder Position. Solange niemand sie
 *   bewegt hat, folgt sie auch einer neuen Größe von Viewport oder `insets` mit `goHome`, sonst
 *   wird nur geklemmt.
 * - `insets` (`{ top, bottom }`, Pixel): von Overlays verdeckte Streifen (Teilnehmer-Köpfe, Dock in
 *   Ruhe). `goHome`, `goOverview` und `minZoom` passen ihr Rechteck in den Bereich dazwischen
 *   (`fitCamera`).
 * - Zoom zwischen `minZoom` (Übersicht passt in den Viewport) und `maxZoom` 2; der Mittelpunkt
 *   bleibt in der Übersicht, damit das Brett nicht aus dem Bild geschoben werden kann.
 * - `handlers` gehören auf das Brett-Element: ein Zeiger auf freier Fläche schwenkt,
 *   zwei Zeiger zoomen (Pinch) um ihre Mitte. Zeiger, die auf einem Element mit `data-no-pan`
 *   beginnen (Brettkarten), und Maustasten außer der linken werden ignoriert.
 * - Das Mausrad zoomt um den Zeiger; Zeilen- und Seiten-Schritte (`deltaMode` 1/2) zählen als
 *   16 px bzw. Viewport-Höhe. Es hängt als nicht-passiver Listener direkt am Element, weil
 *   React Rad-Ereignisse passiv registriert und `preventDefault` dort wirkungslos wäre —
 *   ohne das zoomte Strg+Rad bzw. die Touchpad-Geste die ganze Seite.
 * - `centerOn(worldPt)` rückt einen Weltpunkt in die Mitte und behält den Zoom (Sprung zu einer
 *   Karte, etwa vom Ping-Toast); geklemmt wie jede andere Lage.
 * - `toWorld({ x, y })` nimmt Client-Koordinaten (clientX/clientY), `toScreen(worldPt)` liefert
 *   Pixel relativ zur linken oberen Ecke des Brett-Elements. Beide liefern `null`, solange
 *   `camera` `null` ist.
 * - `touch-action: none` setzt die CSS der Brettfläche, nur dort.
 */
export function useCamera({ viewportRef, playerCount, viewerPosition, insets = NO_INSETS }) {
  const [element, setElement] = useState(null);
  const [viewport, setViewport] = useState({ width: 0, height: 0 });
  const [camera, setCamera] = useState(null);
  const cameraRef = useRef(null);
  const homedKeyRef = useRef(null);
  // Kamera der letzten selbsttätigen Heimansicht: ist sie noch die aktuelle, hat niemand bewegt.
  const autoHomeRef = useRef(null);
  const pointersRef = useRef(new Map());
  const gestureRef = useRef(null);

  const rotation = getViewRotation(viewerPosition, playerCount);
  const ready = viewport.width > 2 * FIT_PADDING_PX && viewport.height > 2 * FIT_PADDING_PX;
  const overview = getOverviewRect(playerCount);
  const insetTop = insets.top || 0;
  const insetBottom = insets.bottom || 0;
  const fitInsets = { top: insetTop, bottom: insetBottom };
  const minZoom = ready ? fitCamera(overview, viewport, rotation, FIT_PADDING_PX, fitInsets).zoom : null;

  const limitsRef = useRef(null);
  limitsRef.current = { rotation, ready, overview, minZoom, viewport, playerCount, viewerPosition, insets: fitInsets };

  const bindViewport = useCallback((node) => {
    if (viewportRef) viewportRef.current = node;
    setElement(node);
  }, [viewportRef]);

  const commit = useCallback((next) => {
    cameraRef.current = next;
    setCamera(next);
  }, []);

  const clampZoom = useCallback((zoom) => {
    const { minZoom: min } = limitsRef.current;
    return Math.min(Math.max(zoom, min), Math.max(MAX_ZOOM, min));
  }, []);

  const normalize = useCallback((cam) => {
    const center = clampToRect(cam, limitsRef.current.overview);
    return { x: center.x, y: center.y, zoom: clampZoom(cam.zoom) };
  }, [clampZoom]);

  const fitTo = useCallback((rect) => {
    const { ready: isReady, viewport: vp, rotation: rot, insets: inset } = limitsRef.current;
    if (!isReady) return;
    commit(normalize(fitCamera(rect, vp, rot, FIT_PADDING_PX, inset)));
  }, [commit, normalize]);

  const goHome = useCallback(() => {
    const { viewerPosition: pos, playerCount: count } = limitsRef.current;
    fitTo(getHomeRect(pos, count));
  }, [fitTo]);

  const goOverview = useCallback(() => {
    fitTo(limitsRef.current.overview);
  }, [fitTo]);

  const centerOn = useCallback((worldPt) => {
    const cam = cameraRef.current;
    if (!cam || !worldPt) return;
    commit(normalize({ x: worldPt.x, y: worldPt.y, zoom: cam.zoom }));
  }, [commit, normalize]);

  useEffect(() => {
    if (!element) return undefined;
    const measure = () => {
      const r = element.getBoundingClientRect();
      setViewport((prev) => (prev.width === r.width && prev.height === r.height ? prev : { width: r.width, height: r.height }));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [element]);

  useEffect(() => {
    if (!ready) return;
    const key = `${playerCount}:${viewerPosition}`;
    const untouched = cameraRef.current !== null && cameraRef.current === autoHomeRef.current;
    if (homedKeyRef.current !== key || !cameraRef.current || untouched) {
      homedKeyRef.current = key;
      goHome();
      autoHomeRef.current = cameraRef.current;
    } else {
      commit(normalize(cameraRef.current));
    }
  }, [ready, viewport.width, viewport.height, insetTop, insetBottom, playerCount, viewerPosition, goHome, commit, normalize]);

  /** Client-Koordinaten → Bildschirmpunkt relativ zur Viewport-Mitte (Bezug von boardGeometry). */
  const toCentered = useCallback((clientX, clientY) => {
    if (!element) return null;
    const r = element.getBoundingClientRect();
    return { x: clientX - r.left - r.width / 2, y: clientY - r.top - r.height / 2 };
  }, [element]);

  const startGesture = useCallback(() => {
    const cam = cameraRef.current;
    const pts = [...pointersRef.current.values()];
    const { rotation: rot } = limitsRef.current;
    if (!cam || pts.length === 0) {
      gestureRef.current = null;
    } else if (pts.length === 1) {
      gestureRef.current = { type: 'pan', world: screenToWorld(pts[0], cam, rot) };
    } else {
      const [a, b] = pts;
      const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      gestureRef.current = {
        type: 'pinch',
        world: screenToWorld(mid, cam, rot),
        startDist: Math.max(1, Math.hypot(a.x - b.x, a.y - b.y)),
        startZoom: cam.zoom,
      };
    }
  }, []);

  const onPointerDown = useCallback((e) => {
    if (!cameraRef.current) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    if (e.target?.closest?.('[data-no-pan]')) return;
    const pt = toCentered(e.clientX, e.clientY);
    if (!pt) return;
    pointersRef.current.set(e.pointerId, pt);
    e.currentTarget.setPointerCapture?.(e.pointerId);
    startGesture();
  }, [toCentered, startGesture]);

  const onPointerMove = useCallback((e) => {
    if (!pointersRef.current.has(e.pointerId)) return;
    const pt = toCentered(e.clientX, e.clientY);
    const gesture = gestureRef.current;
    const cam = cameraRef.current;
    if (!pt || !gesture || !cam) return;
    pointersRef.current.set(e.pointerId, pt);
    const { rotation: rot } = limitsRef.current;
    if (gesture.type === 'pan') {
      commit(normalize(cameraKeepingPoint(gesture.world, pt, cam.zoom, rot)));
      return;
    }
    const [a, b] = [...pointersRef.current.values()];
    const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    const zoom = clampZoom(gesture.startZoom * (Math.hypot(a.x - b.x, a.y - b.y) / gesture.startDist));
    commit(normalize(cameraKeepingPoint(gesture.world, mid, zoom, rot)));
  }, [toCentered, commit, normalize, clampZoom]);

  const onPointerEnd = useCallback((e) => {
    if (!pointersRef.current.delete(e.pointerId)) return;
    startGesture();
  }, [startGesture]);

  useEffect(() => {
    if (!element) return undefined;
    const onWheel = (e) => {
      const cam = cameraRef.current;
      if (!cam) return;
      e.preventDefault();
      const pt = toCentered(e.clientX, e.clientY);
      if (!pt) return;
      const { rotation: rot, viewport: vp } = limitsRef.current;
      const unitPx = { 1: WHEEL_LINE_PX, 2: vp.height }[e.deltaMode] ?? 1;
      const deltaPx = e.deltaY * unitPx;
      const zoom = clampZoom(cam.zoom * Math.exp(-deltaPx * WHEEL_ZOOM_PER_PX));
      const world = screenToWorld(pt, cam, rot);
      commit(normalize(cameraKeepingPoint(world, pt, zoom, rot)));
    };
    element.addEventListener('wheel', onWheel, { passive: false });
    return () => element.removeEventListener('wheel', onWheel);
  }, [element, toCentered, clampZoom, commit, normalize]);

  const toWorld = useCallback((clientPt) => {
    const cam = cameraRef.current;
    const pt = cam && toCentered(clientPt.x, clientPt.y);
    return pt ? screenToWorld(pt, cam, limitsRef.current.rotation) : null;
  }, [toCentered]);

  const toScreen = useCallback((worldPt) => {
    const cam = cameraRef.current;
    if (!cam) return null;
    const { viewport: vp, rotation: rot } = limitsRef.current;
    const p = worldToScreen(worldPt, cam, rot);
    return { x: p.x + vp.width / 2, y: p.y + vp.height / 2 };
  }, []);

  return {
    bindViewport,
    camera,
    rotation,
    minZoom,
    maxZoom: MAX_ZOOM,
    viewport,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: onPointerEnd,
      onPointerCancel: onPointerEnd,
    },
    goHome,
    goOverview,
    centerOn,
    toWorld,
    toScreen,
  };
}
